import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import EditPencil from "../../assets/img/edit-pencil.svg";
import ArchiveSvg from "../../assets/img/archive.svg";
import PublicateSvg from "../../assets/img/publicate.svg";
import { getI18n, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../../components/Button/Button";
import EmptyTable from "../../components/EmptyTable/EmptyTable";
import { RoutesRouter } from "../../models/Routes";
import { PopulatedIssue } from "../../models/IssueModels";
import { CardIssue } from "../../components/CardIssue/CardIssue";
import { KeyStorage, getStorageItem } from "../../utils/localStorage";
import { ITemplateButtonOptions } from "../Survey/Template/TemplatePage";
import { useEndpoints } from "../../hooks/useEndpoints";
import { useComponentVisibles } from "../../utils/functions";
import Chevron from "../../components/Forms/FormInput/FormSelect/Chevron/Chevron";
import getClasses from "../../utils/getClasses";
import { Category } from "../../models/SurveyModels";
import Skeleton from "react-loading-skeleton";
import { useToastAlert } from "../../components/ToastAlert/ToastAlert";
import { DURATION_TOAST } from "../../utils/costants";
require("./IssuePage.scss");

const IssuePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToastAlert();

  const { getAllIssue, patchIssue, getCategories } = useEndpoints();
  const [issues, setIssues] = useState<PopulatedIssue[] | undefined>();
  const [filterCategory, setFilterCategory] = useState<
    "" | "CRITICAL" | "CHAMPION"
  >("");
  const [isLoading, setIsLoading] = useState(true);
  const [inputValue, setInputValue] = React.useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };
  const [arrayFilterSubCategory, setArrayFilterSUbCategory] = useState<
    Category[]
  >([]);
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const handleRefAssignment =
    (index: number) => (ref: HTMLDivElement | null) => {
      refs.current[index] = ref;
    };
  const { isComponentVisible, setComponentVisible } = useComponentVisibles(
    false,
    refs
  );

  const [filterSubCategory, setFilterSUbCategory] = useState<Category>({
    name: t("home.library.filters.allCategories"),
    background: "",
    color: "",
    description: "",
    icon: "",
    id: "",
    coreArea: undefined,
  });
  useEffect(() => {
    getCategories({
      language: getI18n().language.includes("it") ? "ITALIAN" : "ENGLISH",
    }).then((resp) => {
      const filterCategory = [
        {
          name: t("home.library.filters.allCategories"),
          id: "",
          icon: "",
          color: "",
          background: "",
          description: "",
        },
        {
          name: t("home.library.filters.archived"),
          id: "",
          icon: "",
          color: "",
          background: "",
          description: "",
        },
      ];
      setArrayFilterSUbCategory([...filterCategory, ...resp]);
    });
  }, [getCategories, t]);
  useEffect(() => {
    setIsLoading(true);
    getAllIssue({
      category:
        filterSubCategory.name === t("home.library.filters.archived")
          ? "archived"
          : filterSubCategory.id,
      name: debouncedInputValue,
      severity: filterCategory.toLowerCase(),
    }).then((resp) => {
      setIssues(resp);
      setIsLoading(false);
    });
  }, [
    filterSubCategory.name,
    t,
    getAllIssue,
    debouncedInputValue,
    filterCategory,
    filterSubCategory.id,
  ]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);
  const generateButtons = useCallback(
    (
      callback: (
        value: NonNullable<any>,
        role: KeyStorage["me"]["role"]
      ) => ITemplateButtonOptions
    ): Array<ITemplateButtonOptions> => {
      if (!issues) return [] as Array<ITemplateButtonOptions>;
      return issues.map((company) => {
        return callback(company, getStorageItem("me")!.role);
      });
    },
    [issues]
  );

  const changeKeyById = useCallback(
    (id: string): void => {
      patchIssue(id).then((res) => {
        if (res.status === 200) {
          const issue = issues?.find((issue) => issue.id !== id);
          showToast({
            message: `${t(
              `home.issue.toast.${issue?.enabled ? "archived" : "published"}`
            )}`,
            type: "success",
            duration: DURATION_TOAST,
          });
          setIssues((prevLibraries) => {
            const updatedTemplate = prevLibraries?.filter(
              (library) => library.id !== id
            );
            return updatedTemplate;
          });
        }
      });
    },
    [issues, patchIssue, showToast, t]
  );

  const buttonsOption = useMemo(
    () =>
      generateButtons((resource: PopulatedIssue) => {
        let buttons: ITemplateButtonOptions = [];
        buttons.push(
          {
            onClick: (index: number) => {
              navigate(
                `${RoutesRouter.issue}/${issues && issues[index].id}/edit`
              );
            },
            label: `${t("home.edit")}`,
            colorTheme: "blue",
            icon: <img src={EditPencil} alt="edit" />,
          },
          {
            onClick: (index: number) => {
              changeKeyById(resource.id);
            },
            label: `${t(
              `home.library.actions.${
                resource.enabled ? "archive" : "publicate"
              }`
            )}`,
            colorTheme: "blue",
            icon: resource.enabled ? (
              <img src={ArchiveSvg} alt="edit" />
            ) : (
              <img src={PublicateSvg} alt="edit" />
            ),
          }
        );
        return buttons;
      }),
    [changeKeyById, generateButtons, issues, navigate, t]
  );
  const filtersLine = () => (
    <>
      <div className="resources-searching container-filters my-3">
        <div className="form-group">
          <div className="col-lg-6 position-relative">
            <input
              id="searchResource"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              className="form-control filter-questions"
              placeholder={t("home.questions.inputPlaceholder") as string}
            />
            <i className="icon-search"></i>
            {inputValue && (
              <i
                className="icon-close"
                onClick={() => {
                  setInputValue("");
                }}
              ></i>
            )}
          </div>
        </div>
      </div>
      <div className="container-filters">
        <div className="col-lg-12 col-xl-6 resources-searching row container__top">
          <div
            ref={handleRefAssignment(1)}
            className="filter-container col-lg-3 col-xl-3"
          >
            <div
              className="filter-wrapper col-lg-12 col-xl-12"
              onClick={() => {
                setComponentVisible(1);
              }}
            >
              <span className="language-menu filters-label capitalize-first-letter">
                {/*    {t("home.questions.filterActive")}:{" "} */}
                {t(
                  `home.library.filters.${
                    filterCategory === "" ? "all" : filterCategory
                  }`
                )}
              </span>

              <Chevron direction={isComponentVisible[1] ? "top" : "bottom"} />
            </div>
            {isComponentVisible[1] && (
              <div className="switch-filters">
                {(["", "CRITICAL", "CHAMPION"] as const).map((item) => (
                  <div
                    className={`filters-item ${getClasses({
                      active: item === filterCategory,
                    })}`}
                    key={item}
                  >
                    <div
                      onClick={() => {
                        setFilterCategory(item);
                        setComponentVisible(1);
                      }}
                      className="menu-item d-flex justify-content-between gap-1"
                    >
                      <span>
                        {t(
                          `home.library.filters.${item === "" ? "all" : item}`
                        )}
                      </span>
                      {item === filterCategory && (
                        <i className="icon icon-checkmark icon-square-checked me-1"></i>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            ref={handleRefAssignment(2)}
            className="filter-container col-lg-5 col-xl-4"
          >
            <div
              className="filter-wrapper col-lg-12 col-xl-12"
              onClick={() => {
                setComponentVisible(2);
              }}
            >
              <span className="language-menu filters-label capitalize-first-letter">
                {/*                 {t("home.questions.filterActive")}:{" "} */}
                <>{filterSubCategory?.name || t(`selectOptions.ALL`)}</>
              </span>

              <Chevron direction={isComponentVisible[2] ? "top" : "bottom"} />
            </div>
            {isComponentVisible[2] && (
              <div className="switch-filters">
                {arrayFilterSubCategory.map((item, index) => (
                  <div
                    className={`filters-item ${getClasses({
                      active: item.name === filterSubCategory?.name,
                    })}`}
                    key={index}
                  >
                    <div
                      onClick={() => {
                        setFilterSUbCategory(item);
                        setComponentVisible(2);
                      }}
                      className="menu-item d-flex justify-content-between gap-1"
                    >
                      <div>
                        <i
                          style={{
                            color: item.color,
                          }}
                          className={`icon ${item.icon}`}
                        ></i>
                        <span>{item.name}</span>
                      </div>
                      {item.name === filterSubCategory?.name && (
                        <i className="icon icon-checkmark icon-square-checked me-1"></i>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="container-custom library-container questions-container-page issue-page">
      <div className="container__top">
        <div>
          <h1 className="title-page"> {t("home.issue.issue")}</h1>
          <p>{t("home.survey.description")}</p>
        </div>
        <div className="mt-3">
          <Button outline onClick={() => navigate(RoutesRouter.addIssue)}>
            {t("surveyTemplate.create.createButton")}{" "}
            <span className="plus-char">+</span>
          </Button>
        </div>
      </div>
      {filtersLine()}
      <div className="container-issue">
        {!isLoading && issues && issues.length > 0 && (
          <div className=" row wrapper-template-items">
            <CardIssue issues={issues || []} buttonsOption={buttonsOption} />
          </div>
        )}
        {!isLoading && issues?.length === 0 && (
          <EmptyTable
            title={`${t(
              `issue.filters.${filterCategory === "" ? "all" : filterCategory}`
            )}`}
            description={t("issue.emptyTable")}
          />
        )}
        {isLoading && (
          <Skeleton
            count={16}
            width={"24%"}
            height={180}
            inline
            containerClassName="container-template-skeleton"
          />
        )}
      </div>
    </div>
  );
};

export default IssuePage;
